/**
 * Lightweight implementation of the JDL data fusion model levels (0-4) over
 * Iris's data sources. Used to render an explainable status panel in the UI
 * showing what each fusion level is currently doing.
 */
export type JdlLevel = 0 | 1 | 2 | 3 | 4;

export interface JdlStatus {
  level: JdlLevel;
  label: string;
  description: string;
  current_activity: string;
  throughput_per_min: number;
  health: 'green' | 'amber' | 'red';
}

export const JDL_LEVELS: JdlStatus[] = [
  {
    level: 0,
    label: 'L0 - Sub-Object Assessment',
    description:
      'Signal-level processing: noise filtering, calibration, format normalisation across all ingested feeds.',
    current_activity:
      'Normalising 14 feed types into common spatiotemporal frame; processing 4.2k events/min.',
    throughput_per_min: 4200,
    health: 'green',
  },
  {
    level: 1,
    label: 'L1 - Object Assessment / Entity Resolution',
    description:
      'Probabilistic correlation of detections across heterogeneous sources into unified entities.',
    current_activity:
      'Resolving 2,184 active vessel/aircraft/asset entities; 64 active duplicates in deconfliction queue.',
    throughput_per_min: 1100,
    health: 'green',
  },
  {
    level: 2,
    label: 'L2 - Situation Assessment',
    description:
      'Aggregating entity intelligence into a coherent situational picture; pattern matching to known TTPs.',
    current_activity:
      '12 active situations under assessment; 3 elevated to L3 impact analysis.',
    throughput_per_min: 84,
    health: 'amber',
  },
  {
    level: 3,
    label: 'L3 - Impact Assessment',
    description:
      'Forward projection of observed activity to project consequences and probability of escalation.',
    current_activity:
      'Running cascade for ALR-2026-05-02-0003 (vessel proximity); ETA 12s.',
    throughput_per_min: 16,
    health: 'amber',
  },
  {
    level: 4,
    label: 'L4 - Process Refinement',
    description:
      'Meta-analysis: which sensors to retask, where collection gaps exist.',
    current_activity:
      'Recommending Sentinel-1 SAR retasking over Gulf of Finland; 2 gap reports drafted.',
    throughput_per_min: 4,
    health: 'green',
  },
];
